import { Component, ChangeDetectionStrategy, input, output } from "@angular/core";
import { TranslateModule } from "@ngx-translate/core";
import { OverlayModule, ConnectedPosition } from "@angular/cdk/overlay";
import { A11yModule } from "@angular/cdk/a11y";

import { SidenavSectionComponent } from "../sidenav-section/sidenav-section.component";
import { SidenavGroup } from "../sidenav-nav.model";

/**
 * A collapsible admin group (Super User / Developer). Renders a toggle row that:
 *  - when expanded, reveals its sections inline as an accordion;
 *  - when collapsed, opens a hover fly-out containing the same sections.
 *
 * Because the sections render in TWO places (fly-out + inline), the group takes
 * them as DATA and stamps `app-sidenav-section` in both spots rather than using
 * `<ng-content>` (which can't project to two slots).
 *
 * Presentational only: all open / accordion / fly-out STATE lives in the parent
 * home view. The group reflects state via inputs and emits intent via outputs.
 */
@Component({
    selector: "app-sidenav-group",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslateModule, OverlayModule, A11yModule, SidenavSectionComponent],
    templateUrl: "./sidenav-group.component.html",
    styleUrl: "./sidenav-group.component.scss",
})
export class SidenavGroupComponent {

    // =========================================================================
    // INPUTS
    // =========================================================================
    public readonly group = input.required<SidenavGroup>();
    public readonly expanded = input.required<boolean>();
    /** Inline collapsible open state (expanded rail). */
    public readonly open = input.required<boolean>();
    /** Whether a child route is currently active (highlight + pinned-open). */
    public readonly active = input.required<boolean>();
    /** Whether THIS group's collapsed-rail fly-out is open. */
    public readonly flyoutOpen = input.required<boolean>();
    public readonly flyoutPositions = input.required<ConnectedPosition[]>();

    // =========================================================================
    // OUTPUTS - intent (state handled by the parent)
    // =========================================================================
    /** Toggle row clicked while expanded. */
    public readonly toggle = output<void>();
    /** Hover / click while collapsed — open this group's fly-out. */
    public readonly openFlyoutRequest = output<void>();
    /** Pointer left the trigger / fly-out — schedule a debounced close. */
    public readonly scheduleClose = output<void>();
    /** Pointer re-entered the fly-out — cancel the pending close. */
    public readonly cancelClose = output<void>();
    /** Close the fly-out immediately (Escape). */
    public readonly closeFlyout = output<void>();
}
